import * as React from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, Download, ListFilter, Search, Ticket as TicketIcon } from 'lucide-react';
import {
  Avatar,
  Button,
  PageHeader,
  DropdownSelect,
  EmptyState,
  ErrorState,
  FilterChipRow,
  Input,
  Pagination,
  PriorityPill,
  SkeletonRow,
  SlaIndicator,
  StatusPill,
  TagList,
  Tabs,
  formatDuration,
  type SlaState,
} from '@/components/ui';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import { SavedViews } from '@/components/tickets/saved-views';
import { TicketFiltersDrawer, EMPTY_TICKET_FILTERS, countActiveFilters, type TicketFilterState } from '@/components/tickets/ticket-filters-drawer';
import { ExportTicketsDrawer } from '@/components/tickets/export-tickets-drawer';
import { api } from '@/lib/api-client';
import { localizeLabel, resolvePriority, resolveStatus } from '@/lib/constants';
import { getTicketFields, getTicketFieldOptions } from '@/lib/ticket-field-registry-api';
import { cached } from '@/lib/api-cache';
import { useAuth } from '@/lib/auth';
import { useI18n, type TranslationKey } from '@/lib/i18n';

// ─── Types ────────────────────────────────────────────────────────────────────

type TabKey = 'all' | 'due_today' | 'overdue_today' | 'overdue_tomorrow' | 'overdue';
type Layout = 'inbox' | 'card' | 'table';
type SortField = 'createdAt' | 'updatedAt' | 'priority' | 'status' | 'dueBy';
type SortOrder = 'asc' | 'desc';

interface TicketListItem {
  id: string;
  ticketNumber: number;
  subject: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  dueBy: string | null;
  dueByOverridden?: boolean;
  requester: { id: string; firstName: string; lastName: string; email: string } | null;
  assignee: { id: string; firstName: string; lastName: string; email: string } | null;
  group: { id: string; name: string } | null;
  tags: { tag: { id: string; name: string } }[];
}

interface TicketListResponse {
  data: TicketListItem[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type Translator = ReturnType<typeof useI18n>['t'];

function formatRelative(iso: string, t: Translator): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return t('common.justNow');
  if (min < 60) return t('common.minutesAgo', { count: min });
  const hr = Math.round(min / 60);
  if (hr < 24) return t('common.hoursAgo', { count: hr });
  const day = Math.round(hr / 24);
  return t('common.daysAgo', { count: day });
}

function requesterName(t: TicketListItem, translate: Translator): string {
  return t.requester ? `${t.requester.firstName} ${t.requester.lastName}`.trim() : translate('tickets.unknownRequester');
}

/**
 * UI_BUILD_SPEC §2.20 states: a due date alone cannot distinguish "on track"
 * from "at risk", so anything inside the warning window is surfaced as at-risk.
 * There is no per-ticket SLA start timestamp on this list payload, so the ratio
 * rule is approximated with a fixed 2h window.
 * ponytail: fixed window, not 75%-elapsed. Upgrade when /tickets returns the
 * SLA clock start so resolveSlaState can do the real ratio.
 */
const AT_RISK_WINDOW_MS = 2 * 60 * 60 * 1000;

export function slaFor(ticket: Pick<TicketListItem, 'status' | 'dueBy' | 'dueByOverridden'>, t: Translator): { state: SlaState; value: string } | null {
  if (!ticket.dueBy) return null;
  if (!ticket.dueByOverridden && ['resolved', 'closed'].includes(ticket.status)) {
    return { state: 'met', value: t('ticketDetail.done') };
  }

  const remaining = new Date(ticket.dueBy).getTime() - Date.now();
  if (Number.isNaN(remaining)) return null;
  if (remaining < 0) return { state: 'breached', value: t('tickets.overdueBy', { duration: formatDuration(remaining) }) };
  if (remaining <= AT_RISK_WINDOW_MS) return { state: 'at_risk', value: t('tickets.dueIn', { duration: formatDuration(remaining) }) };
  return { state: 'on_track', value: t('tickets.dueIn', { duration: formatDuration(remaining) }) };
}

// ─── Config ───────────────────────────────────────────────────────────────────

const TABS: { key: TabKey; label: TranslationKey }[] = [
  { key: 'all', label: 'tickets.tabAll' },
  { key: 'due_today', label: 'tickets.tabDueToday' },
  { key: 'overdue', label: 'tickets.tabOverdue' },
  { key: 'overdue_tomorrow', label: 'tickets.tabDueTomorrow' },
];

const LAYOUT_KEYS: Record<Layout, TranslationKey> = {
  card: 'tickets.layoutCard',
  inbox: 'tickets.layoutInbox',
  table: 'tickets.layoutTable',
};

const SORT_KEYS: Record<SortField, TranslationKey> = {
  createdAt: 'tickets.sortDateCreated',
  updatedAt: 'tickets.sortDateUpdated',
  priority: 'tickets.sortPriority',
  status: 'tickets.sortStatus',
  dueBy: 'tickets.sortDueDate',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function TicketListPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { t, tOr } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tickets, setTickets] = React.useState<TicketListItem[]>([]);
  const [meta, setMeta] = React.useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [loading, setLoading] = React.useState(true);

  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = React.useState(false);
  const [bulkAssignAgent, setBulkAssignAgent] = React.useState('');
  const [agents, setAgents] = React.useState<SearchableSelectOption[]>([]);
  const [groups, setGroups] = React.useState<SearchableSelectOption[]>([]);
  const [ticketTypes, setTicketTypes] = React.useState<SearchableSelectOption[]>([]);
  const [tags, setTags] = React.useState<SearchableSelectOption[]>([]);
  const [bulkLoading, setBulkLoading] = React.useState(false);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [exportOpen, setExportOpen] = React.useState(false);
  const [bulkExportOpen, setBulkExportOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [activeTab, setActiveTab] = React.useState<TabKey>(() => {
    const due = searchParams.get('due') as TabKey | null;
    return due && ['due_today', 'overdue_today', 'overdue_tomorrow', 'overdue'].includes(due) ? due : 'all';
  });
  const [filters, setFilters] = React.useState<TicketFilterState>(() => ({
    ...EMPTY_TICKET_FILTERS,
    statuses: searchParams.get('status')?.split(',').filter(Boolean) ?? [],
    priorities: searchParams.get('priority')?.split(',').filter(Boolean) ?? [],
    groupIds: searchParams.get('groupId')?.split(',').filter(Boolean) ?? [],
  }));
  const [unassigned, setUnassigned] = React.useState(() => searchParams.get('unassigned') === 'true');
  const [layout, setLayout] = React.useState<Layout>(() => {
    const saved = window.localStorage.getItem('suzukidesk.ticketLayout');
    return saved === 'card' || saved === 'table' || saved === 'inbox' ? saved : 'card';
  });
  const [searchQuery, setSearchQuery] = React.useState(() => searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = React.useState(() => {
    const initialSearch = searchParams.get('search') || '';
    return initialSearch.length >= 2 ? initialSearch : '';
  });
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout>>();
  const listRequestRef = React.useRef<AbortController | null>(null);

  // Debounce search query — 400ms delay + min 2 chars before triggering API
  React.useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    if (searchQuery.length > 0 && searchQuery.length < 2) {
      setDebouncedSearch('');
      return;
    }
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery]);

  const page = Number(searchParams.get('page')) || 1;
  const sort = (searchParams.get('sort') as SortField) || 'createdAt';
  const order = (searchParams.get('order') as SortOrder) || 'desc';

  function setSort(field: SortField) {
    const params = new URLSearchParams(searchParams.toString());
    params.set('sort', field);
    params.set('order', 'desc');
    params.set('page', '1');
    setSearchParams(params);
  }

  function toggleOrder() {
    const params = new URLSearchParams(searchParams.toString());
    params.set('order', order === 'asc' ? 'desc' : 'asc');
    setSearchParams(params);
  }

  // Load agents/groups/ticket-types/tags for filters + bulk-assign.
  React.useEffect(() => {
    cached(
      'dropdown-options-batch',
      () => api.get<{ data: { agents: SearchableSelectOption[]; groups: { value: string; label: string }[] } }>(
        '/dropdown-options/batch',
      ),
    )
      .then((res) => {
        setAgents(res.data.agents);
        setGroups(res.data.groups);
      })
      .catch(() => {});
    api.get<any>('/tags').then((res) => {
      const list = Array.isArray(res) ? res : res?.data ?? [];
      setTags(list.map((t: any) => ({ value: t.id, label: t.name })));
    }).catch(() => {});
    getTicketFields().then(async (fields) => {
      const typeField = fields.find((f) => f.sourceType === 'default_ticket_type');
      if (!typeField) return;
      // System Type options are served by the options endpoint, not rawChoices.
      const choices = typeField.rawChoices?.length
        ? typeField.rawChoices
        : await getTicketFieldOptions(typeField.fieldKey);
      setTicketTypes(choices.map((choice) => ({ value: choice.value, label: choice.label })));
    }).catch(() => {});
  }, []);

  const buildParams = React.useCallback((): Record<string, string | number> => {
    const params: Record<string, string | number> = { page, sort, order };
    if (filters.statuses.length > 0) params.status = filters.statuses.join(',');
    if (filters.priorities.length > 0) params.priority = filters.priorities.join(',');
    if (filters.sources.length > 0) params.source = filters.sources.join(',');
    if (filters.agentIds.length > 0) params.assigneeIds = filters.agentIds.join(',');
    if (filters.groupIds.length > 0) params.groupId = filters.groupIds.join(',');
    if (filters.companyIds.length > 0) params.companyId = filters.companyIds.join(',');
    if (filters.ticketTypeIds.length > 0) params.ticketTypeId = filters.ticketTypeIds.join(',');
    if (filters.tagIds.length > 0) params.tagIds = filters.tagIds.join(',');
    if (filters.createdFrom) params.createdFrom = filters.createdFrom;
    if (filters.createdTo) params.createdTo = filters.createdTo;
    if (filters.resolvedFrom) params.resolvedFrom = filters.resolvedFrom;
    if (filters.resolvedTo) params.resolvedTo = filters.resolvedTo;
    if (filters.closedFrom) params.closedFrom = filters.closedFrom;
    if (filters.closedTo) params.closedTo = filters.closedTo;
    const activeCustomFields = Object.fromEntries(Object.entries(filters.customFields).filter(([, v]) => v.length > 0));
    if (Object.keys(activeCustomFields).length > 0) params.customFieldFilters = JSON.stringify(activeCustomFields);
    if (unassigned) params.unassigned = 'true';
    if (debouncedSearch) params.search = debouncedSearch;
    if (activeTab !== 'all') params.due = activeTab;
    return params;
  }, [filters, unassigned, debouncedSearch, activeTab, page, sort, order]);

  const loadList = React.useCallback(async () => {
    listRequestRef.current?.abort();
    const controller = new AbortController();
    listRequestRef.current = controller;
    setLoading(true);

    try {
      const res = await api.get<TicketListResponse>('/tickets', { params: buildParams(), signal: controller.signal });
      if (controller.signal.aborted) return;
      setTickets(res.data);
      setMeta(res.meta);
      setError(null);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      console.error('Ticket list failed:', err);
      setError(t('tickets.loadFailed'));
      throw err;
    } finally {
      if (listRequestRef.current === controller) {
        listRequestRef.current = null;
        setLoading(false);
      }
    }
  }, [buildParams, t]);

  // Fetch ticket list. A shared controller also cancels an explicit refresh
  // when a newer filter/sort/search request supersedes it.
  React.useEffect(() => {
    void loadList().catch(() => {});
    return () => listRequestRef.current?.abort();
  }, [loadList]);

  React.useEffect(() => {
    window.localStorage.setItem('suzukidesk.ticketLayout', layout);
  }, [layout]);

  // ─── Selection ──────────────────────────────────────────────────────────────

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected(selected.size === tickets.length ? new Set() : new Set(tickets.map((t) => t.id)));
  }

  // ─── Bulk Actions ───────────────────────────────────────────────────────────

  async function handleBulkClose() {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      await api.put('/tickets/bulk', { ticketIds: Array.from(selected), action: 'close' });
      setSelected(new Set());
      setError(null);
      await refreshList();
    } catch (err: any) {
      console.error('Bulk close failed:', err.message);
      setError(t('tickets.bulkCloseFailed'));
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleBulkAssign() {
    if (selected.size === 0 || !bulkAssignAgent) return;
    setBulkLoading(true);
    try {
      await api.put('/tickets/bulk', {
        ticketIds: Array.from(selected),
        action: 'assign',
        assigneeId: bulkAssignAgent,
      });
      setSelected(new Set());
      setBulkAssignOpen(false);
      setBulkAssignAgent('');
      setError(null);
      await refreshList();
    } catch (err: any) {
      console.error('Bulk assign failed:', err.message);
      setError(t('tickets.bulkAssignFailed'));
    } finally {
      setBulkLoading(false);
    }
  }

  async function refreshList() {
    await loadList();
  }

  // ─── Pagination ─────────────────────────────────────────────────────────────

  function goToPage(p: number) {
    const params = new URLSearchParams(searchParams.toString());
    params.set('page', String(p));
    setSearchParams(params);
  }

  const activeFilterCount = countActiveFilters(filters) + (unassigned ? 1 : 0);

  /** §2.18: active filters are shown on the page, not hidden behind a count. */
  const filterChips = React.useMemo(() => {
    const chips: Array<{ key: string; field: string; value: string; onRemove: () => void }> = [];
    for (const status of filters.statuses) {
      chips.push({
        key: `status-${status}`,
        field: t('tickets.chipFieldStatus'),
        value: localizeLabel('status', status, resolveStatus(status).label, tOr),
        onRemove: () => setFilters((prev) => ({ ...prev, statuses: prev.statuses.filter((s) => s !== status) })),
      });
    }
    for (const priority of filters.priorities) {
      chips.push({
        key: `priority-${priority}`,
        field: t('tickets.chipFieldPriority'),
        value: localizeLabel('priority', priority, resolvePriority(priority).label, tOr),
        onRemove: () => setFilters((prev) => ({ ...prev, priorities: prev.priorities.filter((p) => p !== priority) })),
      });
    }
    for (const groupId of filters.groupIds) {
      chips.push({
        key: `group-${groupId}`,
        field: t('tickets.chipFieldGroup'),
        value: groups.find((group) => group.value === groupId)?.label ?? t('tickets.chipValueGroup'),
        onRemove: () => setFilters((prev) => ({ ...prev, groupIds: prev.groupIds.filter((g) => g !== groupId) })),
      });
    }
    if (unassigned) {
      chips.push({ key: 'unassigned', field: t('tickets.chipFieldAssignee'), value: t('tickets.chipValueUnassigned'), onRemove: () => setUnassigned(false) });
    }
    return chips;
  }, [filters, groups, unassigned, t, tOr]);

  function clearAllFilters() {
    setFilters(EMPTY_TICKET_FILTERS);
    setUnassigned(false);
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* No "New ticket" action here — the top bar already carries it globally,
          and two identical primary buttons on one screen breaks the
          one-Primary-per-region rule (UI_BUILD_SPEC §2.1). */}
      <PageHeader
        className="mb-0"
        title={t('tickets.title')}
        description={t('tickets.description')}
      />

      {error && <ErrorState message={error} onRetry={() => void refreshList()} />}

      <Tabs
        aria-label={t('tickets.dueDateTabs')}
        value={activeTab}
        onChange={(value) => setActiveTab(value as TabKey)}
        items={TABS.map((tab) => ({ value: tab.key, label: t(tab.label) }))}
      />

      {/* §4.3: one toolbar. Search moved in from the old right rail so the
          primary way to narrow a queue is not hidden beside it. */}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface p-3 shadow-card">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
          <Input
            type="search"
            aria-label={t('tickets.searchAria')}
            placeholder={t('tickets.searchPlaceholder')}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="h-8 pl-9"
          />
        </div>

        <Button variant="outline" size="sm" onClick={() => setFiltersOpen(true)}>
          <ListFilter aria-hidden="true" />
          {activeFilterCount > 0 ? t('tickets.filtersWithCount', { count: activeFilterCount }) : t('tickets.filters')}
        </Button>

        <DropdownSelect
          triggerVariant="button"
          options={(Object.keys(SORT_KEYS) as SortField[]).map((field) => ({ value: field, label: t(SORT_KEYS[field]) }))}
          value={sort}
          onChange={(v) => setSort(v as SortField)}
          searchable={false}
          aria-label={t('tickets.sortAria')}
          renderTrigger={(selectedOption) => <span>{t('tickets.sortTrigger', { label: selectedOption?.label ?? t('tickets.sortDateCreated') })}</span>}
        />
        <Button
          variant="outline"
          size="icon-sm"
          onClick={toggleOrder}
          aria-label={order === 'asc' ? t('tickets.sortDescending') : t('tickets.sortAscending')}
          title={order === 'asc' ? t('tickets.ascending') : t('tickets.descending')}
        >
          {order === 'asc' ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />}
        </Button>

        <DropdownSelect
          triggerVariant="button"
          options={(Object.keys(LAYOUT_KEYS) as Layout[]).map((value) => ({ value, label: t(LAYOUT_KEYS[value]) }))}
          value={layout}
          onChange={(v) => setLayout(v as Layout)}
          searchable={false}
          align="right"
          aria-label={t('tickets.layoutAria')}
          renderTrigger={(selectedOption) => <span>{t('tickets.layoutTrigger', { label: selectedOption?.label ?? t('tickets.layoutCard') })}</span>}
        />

        <Button variant="outline" size="sm" onClick={() => setExportOpen(true)} disabled={!can('export')}>
          <Download aria-hidden="true" />
          {t('tickets.export')}
        </Button>
      </div>

      <FilterChipRow chips={filterChips} onClearAll={clearAllFilters} />

      {/* Main content: keep the table full-width until the viewport can spare a rail. */}
      <div className="flex min-w-0 flex-col gap-4 2xl:flex-row">
        <div className="min-w-0 flex-1">
          {/* Bulk Actions */}
          {selected.size > 0 && (
            <div className="flex items-center gap-3 rounded-t-md border border-b-0 bg-brand-50 px-4 py-2">
              <span className="text-sm font-medium text-brand-700">{t('tickets.selectedCount', { count: selected.size })}</span>
              {can('assign') && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={bulkLoading}
                  onClick={() => setBulkAssignOpen(!bulkAssignOpen)}
                >
                  {t('tickets.assignAgent')}
                </Button>
              )}
              {can('close') && (
                <Button variant="outline" size="sm" disabled={bulkLoading} onClick={handleBulkClose}>
                  {t('tickets.closeAll')}
                </Button>
              )}
              {can('export') && (
                <Button variant="outline" size="sm" disabled={bulkLoading} onClick={() => setBulkExportOpen(true)}>
                  <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  {t('tickets.exportSelected')}
                </Button>
              )}
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="ml-auto text-xs text-ink-subtle hover:text-ink-muted"
              >
                {t('tickets.clearSelection')}
              </button>
            </div>
          )}

          {/* Bulk assign */}
          {bulkAssignOpen && selected.size > 0 && (
            <div className="flex items-end gap-2 border border-b-0 bg-surface px-4 py-2">
              <div className="flex-1">
                <SearchableSelect
                  options={agents}
                  value={bulkAssignAgent}
                  onChange={setBulkAssignAgent}
                  placeholder={t('tickets.selectAgentPlaceholder')}
                  label={t('tickets.assignTo')}
                />
              </div>
              <Button
                size="sm"
                disabled={!bulkAssignAgent || bulkLoading}
                onClick={handleBulkAssign}
              >
                {t('tickets.assign')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setBulkAssignOpen(false);
                  setBulkAssignAgent('');
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          )}

          {/* Ticket rows */}
          {loading ? (
            <div className="rounded-b-md border border-line bg-surface">
              <SkeletonRow rows={6} />
            </div>
          ) : tickets.length === 0 ? (
            <div className="rounded-b-md border border-line bg-surface">
              {/* §2.21: filtered-empty is a distinct state with its own escape hatch. */}
              {activeFilterCount > 0 || debouncedSearch ? (
                <EmptyState
                  icon={TicketIcon}
                  title={t('tickets.noFilterMatchTitle')}
                  description={t('tickets.noFilterMatchDescription')}
                  action={<Button variant="outline" size="sm" onClick={clearAllFilters}>{t('tickets.clearFilters')}</Button>}
                />
              ) : (
                <EmptyState
                  icon={TicketIcon}
                  title={t('tickets.queueClearTitle')}
                  description={t('tickets.queueClearDescription')}
                  action={can('create') && (
                    <Link to="/tickets/new"><Button size="sm">{t('tickets.newTicket')}</Button></Link>
                  )}
                />
              )}
            </div>
          ) : layout === 'table' ? (
            <div className="min-w-0 max-w-full overflow-x-auto rounded-b-lg border bg-surface">
              <table className="w-full min-w-[900px] table-fixed text-left text-sm">
                <colgroup>
                  <col className="w-10" />
                  <col className="w-[160px]" />
                  <col />
                  <col className="w-[140px]" />
                  <col className="w-[140px]" />
                  <col className="w-[120px]" />
                  <col className="w-[100px]" />
                </colgroup>
                <thead className="border-b bg-surface-hover text-xs uppercase tracking-wide text-ink-subtle">
                  <tr>
                    <th className="w-10 whitespace-nowrap px-4 py-3">
                      <input type="checkbox" checked={tickets.length > 0 && selected.size === tickets.length} onChange={toggleAll} aria-label={t('tickets.selectAllAria')} />
                    </th>
                    <th className="whitespace-nowrap px-4 py-3">{t('tickets.columnContact')}</th>
                    <th className="whitespace-nowrap px-4 py-3">{t('tickets.columnSubject')}</th>
                    <th className="whitespace-nowrap px-4 py-3">{t('tickets.columnState')}</th>
                    <th className="whitespace-nowrap px-4 py-3">{t('tickets.columnGroup')}</th>
                    <th className="whitespace-nowrap px-4 py-3">{t('tickets.columnAgent')}</th>
                    <th className="whitespace-nowrap px-4 py-3">{t('tickets.columnPriority')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {tickets.map((ticket) => (
                    <tr key={ticket.id} className="cursor-pointer hover:bg-surface-hover" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                      <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(ticket.id)} onChange={() => toggleSelect(ticket.id)} aria-label={t('tickets.selectTicketAria', { number: ticket.ticketNumber })} />
                      </td>
                      <td className="min-w-0 max-w-0 overflow-hidden px-4 py-3">
                        <div className="flex min-w-0 max-w-full items-center gap-2 overflow-hidden">
                          <Avatar size="xs" name={requesterName(ticket, t)} id={ticket.requester?.id ?? ticket.id} />
                          <span className="min-w-0 truncate whitespace-nowrap text-ink-muted" title={ticket.requester ? `${ticket.requester.firstName} ${ticket.requester.lastName}` : '—'}>
                            {ticket.requester ? `${ticket.requester.firstName} ${ticket.requester.lastName}` : '—'}
                          </span>
                        </div>
                      </td>
                      <td className="min-w-0 max-w-0 overflow-hidden px-4 py-3">
                        <div className="flex min-w-0 max-w-full items-center overflow-hidden">
                          <span className="min-w-0 flex-1 truncate font-medium text-ink" title={ticket.subject}>{ticket.subject}</span>
                          <span className="ml-2 shrink-0 text-xs text-ink-subtle">#{ticket.ticketNumber}</span>
                        </div>
                      </td>
                      <td className="overflow-hidden px-4 py-3 whitespace-nowrap">
                        <div className="min-w-0 max-w-full overflow-hidden">
                          <StatusPill value={ticket.status} className="max-w-full" />
                        </div>
                      </td>
                      <td className="min-w-0 max-w-0 overflow-hidden px-4 py-3 text-xs text-ink-muted">
                        <span className="block truncate whitespace-nowrap" title={ticket.group?.name ?? '—'}>{ticket.group?.name ?? '—'}</span>
                      </td>
                      <td className="min-w-0 max-w-0 overflow-hidden px-4 py-3 text-xs text-ink-muted">
                        <span className="block truncate whitespace-nowrap" title={ticket.assignee?.firstName ?? '--'}>{ticket.assignee?.firstName ?? '--'}</span>
                      </td>
                      <td className="overflow-hidden px-4 py-3 whitespace-nowrap">
                        <div className="min-w-0 max-w-full overflow-hidden">
                          <PriorityPill value={ticket.priority} className="max-w-full" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : layout === 'card' ? (
            <div className="space-y-2 rounded-b-lg border bg-surface-hover p-3">
              {tickets.map((ticket) => {
                const sla = slaFor(ticket, t);
                return (
                  <div
                    key={ticket.id}
                    className="flex cursor-pointer items-start gap-3 rounded-md border border-line bg-surface px-4 py-3 shadow-card transition-colors hover:border-brand-300 hover:bg-brand-50/40"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                  >
                    <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(ticket.id)} onChange={() => toggleSelect(ticket.id)} className="h-4 w-4 rounded border-line-strong" aria-label={t('tickets.selectTicketAria', { number: ticket.ticketNumber })} />
                    </div>
                    <Avatar className="mt-0.5" name={requesterName(ticket, t)} id={ticket.requester?.id ?? ticket.id} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-medium text-ink">
                        {ticket.subject} <span className="font-mono text-sm font-normal text-ink-subtle">#{ticket.ticketNumber}</span>
                      </p>
                      <p className="mt-0.5 text-sm text-ink-muted">
                        {requesterName(ticket, t)} · {formatRelative(ticket.updatedAt, t)}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {sla && <SlaIndicator state={sla.state} value={sla.value} />}
                        <TagList tags={ticket.tags.map((entry) => entry.tag.name)} max={3} />
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      <StatusPill value={ticket.status} />
                      <PriorityPill value={ticket.priority} />
                      <p className="text-xs text-ink-subtle">{ticket.group?.name ?? t('tickets.noGroup')} · {ticket.assignee?.firstName ?? t('tickets.unassigned')}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="divide-y rounded-b-lg border bg-surface">
              {tickets.map((ticket) => {
                const sla = slaFor(ticket, t);
                return (
                  <div
                    key={ticket.id}
                    className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-brand-50/60"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                  >
                    <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(ticket.id)} onChange={() => toggleSelect(ticket.id)} className="h-4 w-4 rounded border-line-strong" aria-label={t('tickets.selectTicketAria', { number: ticket.ticketNumber })} />
                    </div>
                    <Avatar className="mt-0.5" name={requesterName(ticket, t)} id={ticket.requester?.id ?? ticket.id} />
                    <div className="min-w-0 flex-1">
                      <div className="mb-1">
                        <StatusPill value={ticket.status} />
                      </div>
                      <p className="truncate text-base font-medium text-ink">
                        {ticket.subject} <span className="font-mono text-sm font-normal text-ink-subtle">#{ticket.ticketNumber}</span>
                      </p>
                      <p className="mt-0.5 text-sm text-ink-muted">{requesterName(ticket, t)}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-subtle">
                        <span>{t('tickets.createdRelative', { when: formatRelative(ticket.createdAt, t) })}</span>
                        {sla && <SlaIndicator state={sla.state} value={sla.value} />}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      <PriorityPill value={ticket.priority} />
                      <p className="text-xs text-ink-subtle">{ticket.group?.name ?? t('tickets.noGroup')} · {ticket.assignee?.firstName ?? t('tickets.unassigned')}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && tickets.length > 0 && (
            <Pagination
              className="mt-3"
              page={meta.page}
              pageSize={meta.limit}
              total={meta.total}
              onPageChange={goToPage}
            />
          )}
        </div>

        {/* Saved views rail. Search moved into the main toolbar (§4.3); this
            keeps view management and the unassigned shortcut. */}
        <div className="w-full space-y-4 2xl:w-64 2xl:shrink-0">
          <label className="flex items-center gap-2 text-base text-ink-muted">
            <input
              type="checkbox"
              checked={unassigned}
              onChange={(event) => setUnassigned(event.target.checked)}
              className="h-4 w-4 rounded border-line-strong"
            />
            {t('tickets.unassignedOnly')}
          </label>
          <SavedViews
            currentFilters={{ status: filters.statuses.join(','), priority: filters.priorities.join(','), search: searchQuery }}
            onApply={(applied) => {
              setFilters((prev) => ({
                ...prev,
                statuses: applied.status ? applied.status.split(',').filter(Boolean) : [],
                priorities: applied.priority ? applied.priority.split(',').filter(Boolean) : [],
              }));
              setSearchQuery(applied.search || '');
            }}
          />
        </div>
      </div>

      <TicketFiltersDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        onApply={setFilters}
        agents={agents}
        groups={groups}
        ticketTypes={ticketTypes}
        tags={tags}
      />

      <ExportTicketsDrawer
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        filters={buildParams()}
      />
      <ExportTicketsDrawer
        open={bulkExportOpen}
        onClose={() => setBulkExportOpen(false)}
        filters={{}}
        ticketIds={Array.from(selected)}
      />
    </div>
  );
}
